// The console shell — map centre, left rail and panel, right track stack,
// bottom row. The furniture every client console shares.
//
// WHY THIS IS A COPY AND NOT AN EXTRACTION. The fleet console builds this same
// layout inline in main.ts, and TDI-Brooks runs on it every day. Extracting it
// would mean editing the file their console boots from, to serve a client who
// does not exist for them yet. So the layout is rebuilt here and no fleet file
// is touched. When a second client validates these seams, fleet can migrate onto
// this module deliberately, with its own tests as the guard — and if it never
// does, the cost is one duplicated layout rather than a regression on a live
// client.
//
// The GRID ITSELF IS ALREADY GENERIC. `.appbody` in style.css knows nothing
// about vessels — six named areas and two rows. That is why this module is
// assembly and slots rather than a stylesheet: the CSS was client-agnostic
// before anyone planned a second console. `ui/rail.ts` and `ui/mobiletabs.ts`
// are likewise client-agnostic and are IMPORTED, not copied — reading a shared
// module cannot regress the client that also reads it.
//
// WHAT VARIES, AND IT IS EXACTLY THREE THINGS:
//
//   rail    the left workspaces, and what each one renders
//   centre  what fills the map area
//   stack   the right column's tracks
//
// The bottom row is shared furniture, but WHAT IT GRADES AGAINST is a slot too:
// the fleet console colours it by an armed vessel's limits, and a vessel-less
// client has no such thing. That coupling — with the map's colour ramp and the
// range rings — is why THERMAL_CONSOLE.md says the shell is not really shared
// until the three are inputs. Here they are inputs.

import { createRail, type Rail, type RailItem } from "../../ui/rail.js";
import { createMobileTabs, type MobileTabs } from "../../ui/mobiletabs.js";
import { setupColumns, reclampColumns } from "./columns.js";

export interface ShellSlots {
  /** Left rail workspaces, in order. */
  rail: RailItem[];
  /**
   * Fill one workspace. Called ONCE per workspace, the first time it is opened,
   * with that workspace's own persistent container.
   *
   * Once, not on every select, because `rail.workspace(id)` hands back a
   * container the rail keeps — so a panel that is built once keeps its scroll
   * position, its open disclosures and any half-filled input when the operator
   * switches away and back. Re-rendering on select would silently discard all
   * three.
   */
  renderWorkspace: (id: string, into: HTMLElement) => void;
  /** Fill the centre. Returning a cleanup lets a map tear its listeners down. */
  centre: (into: HTMLElement) => (() => void) | void;
  /** Fill the right column — the track stack. */
  stack: (into: HTMLElement) => (() => void) | void;
  /** Fill the bottom row. Omit and the row is never created, rather than
   *  claiming a grid track and leaving a band of dead space. */
  bottom?: (into: HTMLElement) => (() => void) | void;
  /** Which workspace opens first. Defaults to the first rail item. */
  initial?: string;
  /**
   * Namespace for saved column widths. Give each console its own: one shared key
   * would let a fleet operator's sidebar width land on a thermal client's layout,
   * and — worse — let this console write a preference TDI-Brooks then reads.
   */
  storageNamespace?: string;
  /**
   * Called whenever a region's box changes. MapLibre sizes its canvas from its
   * container and will not notice on its own; anything else that measures itself
   * needs the same nudge.
   */
  onResize?: () => void;
}

export interface Shell {
  body: HTMLElement;
  rail: Rail;
  centre: HTMLElement;
  stack: HTMLElement;
  bottom: HTMLElement | null;
  /** Re-share the column widths — call when a track appears or folds. */
  relayout: () => void;
  destroy: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  title?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (title !== undefined) node.title = title;
  return node;
}

/**
 * Build the shell into `root` and return its regions.
 *
 * Two ordering rules, both of which this layout has been bitten by:
 *
 *  - the mobile tab bar is appended AFTER the body, so it is the last flex child
 *    of the app and cannot overlap the surface above it;
 *  - the bottom row is appended last so it is last in the tab order too. The
 *    grid AREA, not DOM order, is what places it under the centre.
 */
export function createShell(root: HTMLElement, slots: ShellSlots): Shell {
  const body = el("div", "appbody");
  // Read by columns.ts to namespace the saved widths.
  body.dataset["consoleNs"] = slots.storageNamespace ?? "console";
  const centre = el("div", "map");
  const gutter = el("div", "gutter", "Drag to resize · double-click to reset");
  const lgutter = el("div", "gutter lgutter", "Drag to resize · double-click to reset");
  const stack = el("aside", "col2");

  // Assigned after the body is built; the rail's callbacks close over it.
  let mtabs: MobileTabs | null = null;
  const teardown: Array<() => void> = [];
  const built = new Set<string>();

  const paint = (id: string): void => {
    if (built.has(id)) return;
    built.add(id);
    slots.renderWorkspace(id, rail.workspace(id));
  };

  const rail: Rail = createRail({
    items: slots.rail,
    initial: slots.initial,
    onSelect: (id: string) => {
      paint(id);
      // On a phone the panel sits behind a tab; opening a workspace has to bring
      // it forward or the tap reads as dead.
      if (window.matchMedia("(max-width: 880px)").matches) mtabs?.show("panels");
    },
    onPanelToggle: (open: boolean) => {
      // Closing gives the panel's width back to the centre, which has to be told
      // its box changed.
      body.classList.toggle("wsclosed", !open);
      reclampColumns(body);
      slots.onResize?.();
    },
  });

  body.append(rail.rail, rail.panel, lgutter, centre, gutter, stack);

  let bottom: HTMLElement | null = null;
  if (slots.bottom !== undefined) bottom = el("div", "ctable");

  root.appendChild(body);

  const c1 = slots.centre(centre);
  if (typeof c1 === "function") teardown.push(c1);
  const c2 = slots.stack(stack);
  if (typeof c2 === "function") teardown.push(c2);
  if (bottom !== null && slots.bottom !== undefined) {
    body.appendChild(bottom);
    const c3 = slots.bottom(bottom);
    if (typeof c3 === "function") teardown.push(c3);
  }

  // The opening workspace. createRail documents onSelect as NOT firing for the
  // initial selection — deliberately, so a handler touching `rail` cannot run
  // before the binding exists — so painting it here is required, not duplicate.
  const first = rail.selected() ?? slots.initial ?? slots.rail[0]?.id;
  if (first !== undefined && first !== null) paint(first);

  mtabs = createMobileTabs(body);
  root.appendChild(mtabs.root);
  // Marks the shell as tab-driven so the stylesheet can give it a definite
  // height. Set only when the bar was really built, or the stacked fallback gets
  // clipped by a height it never asked for.
  root.classList.add("mtabbed");

  teardown.push(
    setupColumns(body, gutter, lgutter, () => rail.setPanelOpen(false), slots.onResize),
  );

  return {
    body,
    rail,
    centre,
    stack,
    bottom,
    relayout: () => reclampColumns(body),
    destroy: () => {
      for (const fn of teardown.reverse()) {
        try {
          fn();
        } catch {
          // One region's teardown must not strand another's.
        }
      }
      rail.destroy();
    },
  };
}
